import { Controller } from '@hotwired/stimulus';

// Dispatches `invoice-lines:total-changed` whenever the rendered total HT cell
// changes (after a Turbo Stream replace/append/remove on a line row).
//
// Listeners (advance-warning, vat-toggle, amount-validator on the form root)
// pick up the new totalHt via the event detail and recalculate without
// depending on the now-hidden legacy `period_amount_ht_input` field.
//
// Multi-VAT mode is inferred automatically: when `rateTotal` targets are
// present in the DOM (one per active VAT rate, tagged with `data-rate-key`),
// the controller sums per-rate TVA inputs (`vat_amount_5_5`, `vat_amount_10`,
// `vat_amount_20`) and writes each TVA sum into the matching tfoot cell. The
// grand Total HT is back-calculated as Σ(tva × 100 / rate_pct) across all
// active rates. Mono-VAT contracts continue to use the `amount_ht` path unchanged.

/**
 * InvoiceLinesTable Controller
 *
 * Manages live recompute of the Total HT footer cell and per-rate subtotal
 * cells in the invoice lines table.
 *
 * Targets:
 * - totalCell: The `<td>` in `<tfoot>` displaying the grand Total HT.
 * - headerCount: Inline row count displayed next to the section heading.
 * - headerTotal: Inline Total HT summary displayed in the section heading.
 * - readonlyTotal: Mirror element showing Total HT on read-only form surfaces.
 * - rateTotal: Per-rate subtotal `<td>` elements in `<tfoot>`, each carrying
 *   `data-rate-key="<key>"` (e.g. `"5_5"`, `"10"`, `"20"`). Both mono-VAT
 *   (one cell) and multi-VAT (one per active rate) layouts render rateTotal
 *   targets post-Phase 3.
 * - ttcTotalCell: Footer TTC `<td>` = `Total HT + Σ rateTotal`. Live-computed
 *   by `#computeFooterTtc`. SPLIT WITH wps-form: per-row `ttcCell` updates are
 *   owned by `wps-form#computeRowTtc`; the footer aggregate lives here.
 *
 * Values:
 *   (none — mode is inferred from target presence)
 *
 * Actions:
 *   Input events on `[name$="[amount_ht]"]`, `[name$="[vat_amount_5_5]"]`,
 *   `[name$="[vat_amount_10]"]`, `[name$="[vat_amount_20]"]` — fired via the
 *   `input` listener added in `connect()`.
 *
 * Events dispatched:
 * - `invoice-lines:total-changed` — detail: `{ totalHt, count, initial }`
 *   `initial` is TRUE only for the dispatch fired from `connect()` — i.e. the
 *   one NO user gesture produced. Every other dispatch (amount input,
 *   MutationObserver, `recompute()`) is user-driven and carries `false`.
 *   Listeners that own a server-rendered field MUST NOT treat an `initial`
 *   dispatch as an edit: `advance-warning#onTotalChanged` would otherwise
 *   overwrite the architect's persisted « Déduction acompte TTC » with a
 *   connect-time recompute (totalHt: 0 on a mono-invoice statement), and the
 *   resulting explicit 0 is indistinguishable from a deliberate one on submit.
 *   A mutation batch confined to warning strips (`tr[data-testid="row-strip-warning"]`)
 *   dispatches NOTHING: `wps-form` fills the « TVA à vérifier » strip of saved
 *   lines in a microtask after connect, no total moved, and a `false` dispatch
 *   there made `subcontract-cap` wipe the server alert on load (QA K13).
 *
 * @example Mono-VAT (existing behaviour, unchanged)
 * <div data-controller="invoice-lines-table">
 *   <table>
 *     <tbody>
 *       <tr><td><input name="...[amount_ht]"></td></tr>
 *     </tbody>
 *     <tfoot>
 *       <tr><td data-invoice-lines-table-target="totalCell">0,00 €</td></tr>
 *     </tfoot>
 *   </table>
 * </div>
 *
 * @example Multi-VAT (new behaviour)
 * <div data-controller="invoice-lines-table">
 *   <table>
 *     <tbody>
 *       <tr>
 *         <td><input name="...[vat_amount_10]"></td>
 *         <td><input name="...[vat_amount_20]"></td>
 *       </tr>
 *     </tbody>
 *     <tfoot>
 *       <tr>
 *         <td data-invoice-lines-table-target="rateTotal" data-rate-key="10">0,00 €</td>
 *         <td data-invoice-lines-table-target="rateTotal" data-rate-key="20">0,00 €</td>
 *         <td data-invoice-lines-table-target="totalCell">0,00 €</td>
 *       </tr>
 *     </tfoot>
 *   </table>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'totalCell',
    'headerCount',
    'headerTotal',
    'readonlyTotal',
    'rateTotal',
    'ttcTotalCell',
  ];

  connect() {
    // Watch the entire table subtree for any DOM mutation (cell text change,
    // row add/remove). Cheap because the table is small and updates are rare.
    // Warning strips are advisory rows that carry no amount: their writes are
    // not a total change (see the `initial` note in the docblock).
    this.observer = new MutationObserver((records) => {
      if (records.every((record) => this.#insideWarningStrip(record.target))) return;
      this.dispatchTotal();
    });
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.handleAmountInput = this.handleAmountInput.bind(this);
    this.element.addEventListener('input', this.handleAmountInput);

    // Initial dispatch so listeners get the current total on page load.
    // Flagged `initial` — see the class docblock: this is the ONE dispatch that
    // precedes any user gesture, and listeners owning a server-rendered field
    // rely on the flag to tell it apart from a real edit.
    this.dispatchTotal({ initial: true });
  }

  disconnect() {
    this.observer?.disconnect();
    if (this.handleAmountInput) {
      this.element.removeEventListener('input', this.handleAmountInput);
    }
  }

  /**
   * @param {Object} [options]
   * @param {boolean} [options.initial=false] - True ONLY when called from
   *   `connect()`. Defaults to false so every other caller (the
   *   MutationObserver callback, `recompute()`) is user-driven by construction.
   */
  dispatchTotal({ initial = false } = {}) {
    const totalHt = this.parseTotal();
    const count = this.countRows();

    // Pause the observer while we mutate `readonlyTotal` (a child of
    // this.element). Without this, the textContent write below feeds back
    // into the observer and re-enters dispatchTotal — infinite loop.
    this.observer?.disconnect();
    this.updateHeader(count, totalHt);
    this.updateReadonlyMirror(totalHt);
    this.observer?.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.dispatch('total-changed', {
      detail: { totalHt, count, initial },
      prefix: 'invoice-lines',
    });
  }

  /**
   * Handles `input` events on amount fields within the table.
   *
   * - Mono-VAT path: `[name$="[amount_ht]"]` → delegates to
   *   `#recomputeMonoVatTotal` (existing behaviour, unchanged).
   * - Multi-VAT path: `[name$="[amount_vat_<key>]"]` → delegates to
   *   `#recomputeMultiVatTotals(rateKey)` which updates both the per-rate
   *   tfoot cell and the grand Total HT.
   *
   * Multi-VAT mode is detected lazily from `this.rateTotalTargets.length > 0`
   * so the same controller instance serves both layouts without a constructor
   * flag or a separate value.
   *
   * @param {InputEvent} event
   */
  handleAmountInput(event) {
    const name = event.target.name || '';

    // Mono-VAT path — unchanged fallback for contracts with a single amount_ht field.
    if (name.endsWith('[amount_ht]')) {
      this.#recomputeMonoVatTotal();
      return;
    }

    // Multi-VAT path — match [vat_amount_5_5], [vat_amount_10], [vat_amount_20].
    // The rate key is the suffix after the "vat_amount_" prefix.
    // Input name format: work_progress_statement[line_inputs][N][vat_amount_<key>]
    const multiVatMatch = name.match(/\[vat_amount_([^\]]+)\]$/);
    if (multiVatMatch) {
      this.#recomputeMultiVatTotals(multiVatMatch[1]);
    }
  }

  recomputeTotalFromInputs() {
    // Public alias kept for backward compatibility — delegates to the mono-VAT path.
    this.#recomputeMonoVatTotal();
  }

  /**
   * Public recompute — refreshes Total HT and per-rate cells from current
   * inputs. Handles mono-VAT, multi-VAT, and empty-table cases. Used by
   * wps-form after non-input row mutations (e.g. removeLine) where the
   * MutationObserver-driven `parseTotal()` would otherwise read stale
   * textContent.
   */
  recompute() {
    if (this.countRows() === 0) {
      this.observer?.disconnect();
      if (this.hasTotalCellTarget) {
        this.totalCellTarget.textContent = this.formatCurrency(0);
      }
      this.rateTotalTargets.forEach((cell) => {
        cell.textContent = this.formatCurrency(0);
      });
      if (this.hasTtcTotalCellTarget) {
        this.ttcTotalCellTarget.textContent = this.formatCurrency(0);
      }
      this.observer?.observe(this.element, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      this.dispatchTotal();
      return;
    }

    // Total HT now comes from sum of [amount_ht] inputs in every layout
    // (multi-VAT rows carry an explicit HT input as of spec 018).
    this.#recomputeMonoVatTotal();
    if (this.hasRateTotalTarget) {
      this.rateTotalTargets.forEach((cell) => this.#updateRateCellForKey(cell.dataset.rateKey));
      this.#computeFooterTtc();
    }
  }

  countRows() {
    // Count visible <tr> rows in the lines tbody. Buffered mode rows carry
    // `data-local-id` (cloned from the form-row template); :live mode rows
    // carry `data-testid="invoice-line-row"` (rendered by InvoiceLineRowComponent).
    return this.element.querySelectorAll(
      'tbody tr[data-local-id], tbody tr[data-testid="invoice-line-row"]'
    ).length;
  }

  updateHeader(count, totalHt) {
    if (this.hasHeaderCountTarget) {
      this.headerCountTarget.textContent = count > 0 ? `(${count})` : '';
    }
    if (this.hasHeaderTotalTarget) {
      if (count > 0) {
        this.headerTotalTarget.textContent = `Total HT : ${this.formatCurrency(totalHt)}`;
        this.headerTotalTarget.classList.remove('hidden');
      } else {
        this.headerTotalTarget.textContent = '';
        this.headerTotalTarget.classList.add('hidden');
      }
    }
  }

  updateReadonlyMirror(totalHt) {
    if (this.hasReadonlyTotalTarget) {
      this.readonlyTotalTarget.textContent = this.formatCurrency(totalHt);
    }
  }

  formatCurrency(amount) {
    // French formatting: `5 030,00 €` — matches Rails `number_to_currency`.
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  }

  parseTotal() {
    if (!this.hasTotalCellTarget) return 0;
    const raw = this.totalCellTarget.textContent.trim();
    // "1 234,56 €" → 1234.56
    const cleaned = raw
      .replace(/[^\d,.-]/g, '')
      .replace(/\s/g, '')
      .replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /**
   * Mono-VAT recompute: sums all `[amount_ht]` inputs across the table and
   * writes the result into `totalCellTarget`, the header, the readonly mirror,
   * and dispatches `invoice-lines:total-changed`.
   *
   * This is the pre-existing behaviour, extracted into a private method so
   * `handleAmountInput` can branch cleanly.
   */
  #recomputeMonoVatTotal() {
    const inputs = this.element.querySelectorAll('input[name$="[amount_ht]"]');
    let sum = 0;
    inputs.forEach((input) => {
      const numeric = parseFloat(String(input.value).replace(',', '.'));
      if (!Number.isNaN(numeric)) sum += numeric;
    });

    this.#writeTotalCells(sum);
  }

  /**
   * Multi-VAT recompute triggered by a change to a per-rate TVA input.
   *
   * Updates the matching `rateTotal` tfoot cell (sum of all `[vat_amount_<key>]`
   * inputs for that rate) and refreshes the footer TTC. Total HT comes from
   * the explicit `[amount_ht]` inputs via `#recomputeMonoVatTotal` — no more
   * back-calculation from TVA, since multi-VAT rows now carry an explicit HT
   * input column.
   *
   * If no `rateTotal` targets exist (mono-VAT layout pre-018), the call is a
   * no-op so the method is safe to call unconditionally from `handleAmountInput`.
   *
   * @param {string} rateKey - e.g. `"5_5"`, `"10"`, `"20"`
   */
  #recomputeMultiVatTotals(rateKey) {
    if (!this.hasRateTotalTarget) return;

    this.#updateRateCellForKey(rateKey);
    // Footer TTC depends on rate sums + Total HT, both of which are now
    // current. Total HT itself doesn't change on a TVA edit.
    this.#computeFooterTtc();
  }

  /**
   * Sums all `[vat_amount_<key>]` TVA inputs across the table and writes the
   * TVA sum into the `rateTotal` target whose `data-rate-key` matches `key`.
   *
   * The observer is paused during the textContent write to avoid re-entrancy.
   *
   * @param {string} key - Rate key, e.g. `"10"`, `"20"`, `"5_5"`
   */
  #updateRateCellForKey(key) {
    const rateCell = this.rateTotalTargets.find((t) => t.dataset.rateKey === key);
    if (!rateCell) return;

    const inputs = this.element.querySelectorAll(`input[name$="[vat_amount_${key}]"]`);
    let sum = 0;
    inputs.forEach((input) => {
      const numeric = parseFloat(String(input.value).replace(',', '.'));
      if (!Number.isNaN(numeric)) sum += numeric;
    });

    this.observer?.disconnect();
    rateCell.textContent = this.formatCurrency(sum);
    this.observer?.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Per-rate cell changed → footer TTC must follow.
    this.#computeFooterTtc();
  }

  /**
   * Compute and write the footer TTC = Total HT + Σ rateTotal.
   *
   * Reads textContent from the already-updated `totalCellTarget` and every
   * `rateTotalTarget`, parses French currency, sums, writes back into
   * `ttcTotalCellTarget`. Called from `#writeTotalCells` (whenever Total HT
   * changes) and from `#updateRateCellForKey` (whenever a per-rate cell
   * changes), so every footer mutation triggers a TTC refresh.
   *
   * SPLIT WITH wps-form: this method owns the FOOTER TTC; per-row TTC is
   * owned by `wps-form#computeRowTtc`. The server pre-renders both for
   * flicker-free initial paint.
   */
  #computeFooterTtc() {
    if (!this.hasTtcTotalCellTarget) return;

    let total = 0;
    if (this.hasTotalCellTarget) {
      total += this.#parseCurrency(this.totalCellTarget.textContent);
    }
    this.rateTotalTargets.forEach((cell) => {
      total += this.#parseCurrency(cell.textContent);
    });

    this.observer?.disconnect();
    this.ttcTotalCellTarget.textContent = this.formatCurrency(total);
    this.observer?.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * Parse a French-formatted currency string ("1 234,56 €") into a number.
   * Returns 0 on parse failure.
   *
   * @param {string} text
   * @returns {number}
   */
  #parseCurrency(text) {
    const cleaned = String(text || '')
      .replace(/[^\d,.-]/g, '')
      .replace(/\s/g, '')
      .replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  /**
   * Writes a computed total into every surface that mirrors Total HT:
   * - `totalCellTarget` tfoot cell
   * - `headerTotalTarget` / `headerCountTarget` section heading
   * - `readonlyTotalTarget` read-only form surface
   * Then dispatches `invoice-lines:total-changed`.
   *
   * The MutationObserver is paused during DOM writes to avoid infinite loops.
   *
   * @param {number} sum - Grand Total HT in euros (numeric, not formatted)
   */
  #writeTotalCells(sum) {
    // Pause the observer while we mutate textContent — totalCellTarget is a
    // child of this.element, so the write would re-enter dispatchTotal otherwise.
    this.observer?.disconnect();
    if (this.hasTotalCellTarget) {
      this.totalCellTarget.textContent = this.formatCurrency(sum);
    }
    this.observer?.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Total HT changed → footer TTC must follow.
    this.#computeFooterTtc();

    const count = this.countRows();
    this.updateHeader(count, sum);
    this.updateReadonlyMirror(sum);
    // `initial: false` — this path is only ever reached from a real amount edit
    // (`handleAmountInput`) or an explicit `recompute()`, never from connect().
    this.dispatch('total-changed', {
      detail: { totalHt: sum, count, initial: false },
      prefix: 'invoice-lines',
    });
  }

  // A mutation target is a text node for `characterData`, an element otherwise.
  #insideWarningStrip(node) {
    const element = node?.closest ? node : node?.parentElement;
    return Boolean(element?.closest('tr[data-testid="row-strip-warning"]'));
  }
}
